import { useMemo } from 'react';
import { capitalizeFirstLetter } from '@/utils/admin/utils';
import { useThemeColors } from '@/hooks/shared/useThemeColors';

/**
 * Theme-aware palette mapping lease status values to semantic colors. Callers
 * pull this via `useLeaseStatusPalette()` and pass values into
 * `getLeaseStatusColor()` so dark/light toggles re-tint status badges.
 */
export interface LeaseStatusPalette {
  current: string;
  notice: string;
  future: string;
  past: string;
  default: string;
}

export const getLeaseStatusColor = (
  status: string,
  palette: LeaseStatusPalette,
): string => {
  const value = (status || '').toLowerCase();
  switch (value) {
    case 'current':
      return palette.current;
    case 'notice':
      return palette.notice;
    case 'future':
      return palette.future;
    case 'past':
    case 'cancelled':
      return palette.past;
    default:
      return palette.default;
  }
};

export const getLeaseStatusLabel = (status: string): string => {
  const value = (status || '').toLowerCase();
  switch (value) {
    case 'current':
      return 'Current Lease';
    case 'past':
      return 'Past Lease';
    case 'future':
      return 'Future Lease';
    case 'notice':
      return 'Lease On Notice';
    default:
      return capitalizeFirstLetter(status || '');
  }
};

export function useLeaseStatusPalette(): LeaseStatusPalette {
  const { semantic } = useThemeColors();
  return useMemo(
    () => ({
      current: semantic.status.success,
      notice: semantic.status.warning,
      future: semantic.status.info,
      past: semantic.status.error,
      default: semantic.text.muted,
    }),
    [
      semantic.status.success,
      semantic.status.warning,
      semantic.status.info,
      semantic.status.error,
      semantic.text.muted,
    ],
  );
}
